import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  FileText,
  Layers,
  Eye,
  UploadCloud,
  History,
  Undo2,
  Monitor,
  Tablet,
  Smartphone,
  Trash2,
  Save,
} from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const VIEWPORTS = {
  desktop: { icon: Monitor, w: "100%", label: "Desktop" },
  tablet: { icon: Tablet, w: "768px", label: "Tablet" },
  mobile: { icon: Smartphone, w: "375px", label: "Mobile" },
};

const KIND_LABELS = {
  content: "Content",
  hero: "Hero",
  cta: "Call to Action",
  faq: "FAQ",
  testimonials: "Testimonials",
  footer: "Footer",
  nav: "Navigation",
  custom: "Custom",
};

const CONTENT_KIND_INPUT = {
  text: "text",
  richtext: "textarea",
  image: "url",
  url: "url",
  number: "number",
  bool: "checkbox",
};

const SiteEditor = () => {
  const { hasPermission } = useAuth();
  const canPublish = hasPermission("documents.manage");
  const [params, setParams] = useSearchParams();
  const initialEntity = params.get("entity") || "";
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState(initialEntity);
  const [pages, setPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [sections, setSections] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [content, setContent] = useState([]);
  const [versions, setVersions] = useState([]);
  const [viewport, setViewport] = useState("desktop");
  const [previewMode, setPreviewMode] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Load entities
  useEffect(() => {
    api.get("/enterprise/entities").then((r) => {
      setEntities(r.data);
      if (!entityId && r.data.length > 0) setEntityId(r.data[0].id);
      setLoading(false);
    }).catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPages = useCallback(async () => {
    if (!entityId) return;
    try {
      const { data } = await api.get("/site/pages", { params: { entity_id: entityId } });
      setPages(data);
      if (data.length > 0 && !data.find((p) => p.id === selectedPageId)) {
        setSelectedPageId(data[0].id);
      } else if (data.length === 0) {
        setSelectedPageId(null);
        setSections([]);
        setContent([]);
      }
    } catch {
      toast.error("Could not load pages");
    }
  }, [entityId, selectedPageId]);

  useEffect(() => {
    loadPages();
    setParams(entityId ? { entity: entityId } : {});
  }, [entityId, loadPages, setParams]);

  const loadSections = useCallback(async () => {
    if (!selectedPageId) return;
    try {
      const { data } = await api.get(`/site/pages/${selectedPageId}/sections`);
      setSections(data);
      if (data.length > 0 && !data.find((s) => s.id === selectedSectionId)) {
        setSelectedSectionId(data[0].id);
      } else if (data.length === 0) {
        setSelectedSectionId(null);
        setContent([]);
      }
      const v = await api.get(`/site/pages/${selectedPageId}/versions`);
      setVersions(v.data);
    } catch (err) {
      // Non-fatal: sections/versions best-effort load.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("loadSections failed", err);
      }
    }
  }, [selectedPageId, selectedSectionId]);

  useEffect(() => { loadSections(); }, [loadSections]);

  const loadContent = useCallback(async () => {
    if (!selectedSectionId) return;
    try {
      const { data } = await api.get(`/site/sections/${selectedSectionId}/content`);
      setContent(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("loadContent failed", err);
      }
    }
  }, [selectedSectionId]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const createPage = async () => {
    const name = window.prompt("Page name (e.g. 'Home'):");
    if (!name) return;
    const slug = (window.prompt("Slug (e.g. 'home'):", name.toLowerCase().replace(/\s+/g, "-")) || "").trim();
    if (!slug) return;
    try {
      const { data } = await api.post("/site/pages", { entity_id: entityId, name, slug });
      toast.success("Page created");
      loadPages();
      setSelectedPageId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const createSection = async () => {
    if (!selectedPageId) return;
    const label = window.prompt("Section label (e.g. 'Hero', 'Services', 'FAQ'):");
    if (!label) return;
    const kind = window.prompt("Kind (content|hero|cta|faq|testimonials|footer|nav|custom):", "content") || "content";
    try {
      const { data } = await api.post("/site/sections", { page_id: selectedPageId, label, kind, order: sections.length });
      toast.success("Section added");
      loadSections();
      setSelectedSectionId(data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    }
  };

  const addContentItem = async () => {
    const key = window.prompt("Content key (e.g. 'title', 'body_1'):");
    if (!key) return;
    const kind = window.prompt("Kind (text|richtext|image|url|number|bool):", "text") || "text";
    try {
      await api.post("/site/content", {
        section_id: selectedSectionId,
        key,
        label: key,
        kind,
        draft_value: kind === "bool" ? false : (kind === "number" ? 0 : ""),
      });
      toast.success("Field added");
      loadContent();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Add failed");
    }
  };

  const saveContent = async (item, value) => {
    try {
      await api.post("/site/content", {
        section_id: item.section_id,
        key: item.key,
        label: item.label,
        kind: item.kind,
        draft_value: value,
      });
      setContent((prev) => prev.map((c) => (c.id === item.id ? { ...c, draft_value: value, state: "draft" } : c)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const publish = async () => {
    if (!selectedPageId) return;
    if (!window.confirm("Publish current draft? A new version will be recorded.")) return;
    setBusy(true);
    try {
      const desc = window.prompt("Version description (optional):", "") || "";
      await api.post(`/site/pages/${selectedPageId}/publish`, null, { params: { description: desc } });
      toast.success("Published.");
      loadContent();
      loadSections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const rollback = async (versionId) => {
    if (!window.confirm("Roll back to this version? A new version entry will be created.")) return;
    setBusy(true);
    try {
      await api.post(`/site/pages/${selectedPageId}/versions/${versionId}/rollback`);
      toast.success("Rolled back");
      loadContent();
      loadSections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setBusy(false);
    }
  };

  const requestPublishApproval = async () => {
    try {
      const page = pages.find((p) => p.id === selectedPageId);
      await api.post("/approvals", {
        entity_id: entityId,
        type: "site_publish",
        title: `Publish page: ${page?.name || selectedPageId}`,
        description: `Request to publish page "${page?.name}" for entity.`,
        priority: "medium",
      });
      toast.success("Submitted for approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      await api.delete(`/site/sections/${id}`);
      toast.success("Deleted");
      loadSections();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const currentPage = useMemo(() => pages.find((p) => p.id === selectedPageId), [pages, selectedPageId]);
  const vp = VIEWPORTS[viewport];

  return (
    <Layout>
      <section className="mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="label-eyebrow">Content Governance</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
            Site & App Editor
          </h1>
          <p className="text-textmuted mt-2 max-w-2xl text-sm">
            Safely edit routine content, navigation, pricing, contact and branding for any
            R.S.V.P. business. Every change is drafted, previewed, versioned and reversible.
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-champagne animate-spin" /></div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 items-center mb-4">
            <label className="label-eyebrow">Business</label>
            <select
              value={entityId}
              onChange={(e) => { setEntityId(e.target.value); setSelectedPageId(null); }}
              data-testid="editor-entity-select"
              className="bg-obsidian border border-seam rounded-md px-3 py-2 text-sm text-textmain focus:outline-none focus:border-champagne"
            >
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Pages */}
            <aside className="lg:col-span-3 regal-card p-4 max-h-[70vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <span className="label-eyebrow">Pages</span>
                <button onClick={createPage} data-testid="add-page-btn" className="p-1 text-champagne hover:bg-elevated rounded" title="New page">
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              {pages.length === 0 ? (
                <p className="text-xs text-textmuted">No pages yet.</p>
              ) : (
                <ul className="space-y-1">
                  {pages.map((p) => (
                    <li key={p.id}>
                      <button
                        onClick={() => setSelectedPageId(p.id)}
                        data-testid={`page-${p.id}`}
                        className={`w-full text-left px-2 py-2 rounded text-sm flex items-center gap-2 ${
                          selectedPageId === p.id ? "bg-elevated border border-champagne/30 text-champagne" : "text-textmuted hover:bg-elevated"
                        }`}
                      >
                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="truncate">{p.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </aside>

            {/* Sections & content */}
            <section className="lg:col-span-5 regal-card p-4 max-h-[70vh] overflow-y-auto">
              {!currentPage ? (
                <p className="text-textmuted text-center py-8">Select or create a page.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="label-eyebrow">Editing</span>
                      <h3 className="font-heading text-2xl">{currentPage.name}</h3>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={createSection} data-testid="add-section-btn" className="text-xs border border-champagne/40 text-champagne px-3 py-1.5 rounded hover:bg-champagne/10 inline-flex items-center gap-1">
                        <Layers className="w-3 h-3" strokeWidth={1.5} /> Add section
                      </button>
                    </div>
                  </div>

                  {sections.map((s) => (
                    <div key={s.id} className="border border-seam rounded-md mb-3">
                      <div
                        className={`px-3 py-2 border-b border-seam flex items-center justify-between cursor-pointer ${
                          selectedSectionId === s.id ? "bg-elevated/60" : "bg-elevated/30"
                        }`}
                        onClick={() => setSelectedSectionId(s.id === selectedSectionId ? null : s.id)}
                      >
                        <div>
                          <p className="text-sm text-textmain">{s.label}</p>
                          <p className="text-[10px] uppercase tracking-wider text-textmuted">{KIND_LABELS[s.kind] || s.kind}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSection(s.id); }}
                          className="p-1 text-textmuted hover:text-[#E5A09E]"
                          title="Delete section"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                      {selectedSectionId === s.id && (
                        <div className="p-3 space-y-3">
                          {content.map((c) => (
                            <div key={c.id}>
                              <label className="block label-eyebrow mb-1">{c.label || c.key}
                                <span className="ml-2 text-[9px] text-champagne">{c.state}</span>
                              </label>
                              {c.kind === "richtext" ? (
                                <textarea
                                  rows={3}
                                  defaultValue={c.draft_value || ""}
                                  onBlur={(e) => saveContent(c, e.target.value)}
                                  data-testid={`content-${c.key}`}
                                  className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm focus:outline-none focus:border-champagne resize-none"
                                />
                              ) : c.kind === "bool" ? (
                                <input
                                  type="checkbox"
                                  defaultChecked={!!c.draft_value}
                                  onChange={(e) => saveContent(c, e.target.checked)}
                                  className="w-4 h-4 accent-champagne"
                                />
                              ) : (
                                <input
                                  type={CONTENT_KIND_INPUT[c.kind] || "text"}
                                  defaultValue={c.draft_value ?? ""}
                                  onBlur={(e) => {
                                    const v = c.kind === "number" ? parseFloat(e.target.value) || 0 : e.target.value;
                                    saveContent(c, v);
                                  }}
                                  data-testid={`content-${c.key}`}
                                  className="w-full bg-obsidian border border-seam rounded-md px-3 py-2 text-sm focus:outline-none focus:border-champagne"
                                />
                              )}
                            </div>
                          ))}
                          <button onClick={addContentItem} data-testid="add-content-btn" className="text-xs text-champagne hover:underline inline-flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add field
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </>
              )}
            </section>

            {/* Preview + actions */}
            <section className="lg:col-span-4">
              <div className="regal-card p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="label-eyebrow flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</span>
                  <div className="flex gap-1">
                    {Object.entries(VIEWPORTS).map(([k, v]) => {
                      const Icon = v.icon;
                      return (
                        <button
                          key={k}
                          onClick={() => setViewport(k)}
                          data-testid={`viewport-${k}`}
                          className={`p-1.5 rounded ${viewport === k ? "bg-elevated border border-champagne/40 text-champagne" : "text-textmuted hover:text-textmain"}`}
                          title={v.label}
                        >
                          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  <button
                    onClick={() => setPreviewMode("draft")}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${previewMode === "draft" ? "bg-champagne/10 text-champagne border border-champagne/40" : "text-textmuted border border-seam"}`}
                  >
                    Draft
                  </button>
                  <button
                    onClick={() => setPreviewMode("published")}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${previewMode === "published" ? "bg-champagne/10 text-champagne border border-champagne/40" : "text-textmuted border border-seam"}`}
                  >
                    Published
                  </button>
                </div>
                <div className="bg-obsidian border border-seam rounded p-3 overflow-x-auto" style={{ minHeight: "220px" }}>
                  <div style={{ width: vp.w, maxWidth: "100%", margin: "0 auto" }}>
                    {sections.length === 0 ? (
                      <p className="text-xs text-textmuted text-center py-8">No sections</p>
                    ) : (
                      sections.map((s) => (
                        <div key={s.id} className="mb-4">
                          <p className="text-[10px] uppercase tracking-wider text-champagne mb-1">{s.label}</p>
                          {content.filter((c) => c.section_id === s.id).map((c) => {
                            const v = previewMode === "draft" ? c.draft_value : c.published_value;
                            if (c.kind === "image" && v) {
                              return <img key={c.id} src={v} alt={c.label} className="max-w-full rounded my-1" />;
                            }
                            return (
                              <p key={c.id} className={`text-sm text-textmain ${c.kind === "richtext" ? "whitespace-pre-wrap leading-relaxed" : ""}`}>
                                {v?.toString?.() || <span className="text-textmuted italic">—</span>}
                              </p>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="regal-card p-4 mb-4 space-y-2">
                <button
                  onClick={requestPublishApproval}
                  disabled={!selectedPageId || busy}
                  data-testid="submit-approval-btn"
                  className="w-full text-sm border border-seam text-textmuted hover:text-textmain px-4 py-2 rounded inline-flex items-center gap-2 justify-center transition-colors disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} /> Submit for approval
                </button>
                {canPublish && (
                  <button
                    onClick={publish}
                    disabled={!selectedPageId || busy}
                    data-testid="publish-btn"
                    className="w-full text-sm bg-champagne text-obsidian font-medium px-4 py-2 rounded inline-flex items-center gap-2 justify-center hover:bg-champagne-soft disabled:opacity-50"
                  >
                    <UploadCloud className="w-3.5 h-3.5" strokeWidth={1.5} /> Publish
                  </button>
                )}
              </div>

              {/* Version history */}
              <div className="regal-card p-4 max-h-64 overflow-y-auto">
                <div className="flex items-center gap-2 mb-2">
                  <History className="w-3.5 h-3.5 text-champagne" strokeWidth={1.5} />
                  <span className="label-eyebrow">Version history</span>
                </div>
                {versions.length === 0 ? (
                  <p className="text-xs text-textmuted">No versions yet.</p>
                ) : (
                  versions.map((v) => (
                    <div key={v.id} className="border-b border-seam last:border-b-0 py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm text-textmain">v{v.version_number}</p>
                        <p className="text-[11px] text-textmuted truncate">{v.description} · {v.published_at?.slice(0, 16).replace("T", " ")}</p>
                      </div>
                      {canPublish && (
                        <button
                          onClick={() => rollback(v.id)}
                          data-testid={`rollback-${v.id}`}
                          className="text-[11px] px-2 py-1 rounded border border-seam text-textmuted hover:text-champagne hover:border-champagne/40 inline-flex items-center gap-1"
                        >
                          <Undo2 className="w-3 h-3" strokeWidth={1.5} /> Roll back
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </Layout>
  );
};

export default SiteEditor;
